import { useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  Bold, ButtonView, ClassicEditor, Essentials, FileRepository, Image, ImageToolbar, ImageUpload, Italic, Link, List, Mention, Paragraph, PasteFromOffice,
  Plugin, Undo,
} from 'ckeditor5';
import { api } from '../../lib/api';
import { notifyError } from '../../lib/notify';
import { downloadFile, isImageUrl, isUploadedFileUrl, openFile } from './fileViewer';
import { useMentionPeople, type MentionPerson } from './MentionContext';

const CLIP_ICON =
  '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M13.5 6.5v6.2a3.5 3.5 0 0 1-7 0V5.6a2.3 2.3 0 0 1 4.6 0v6.8a1.1 1.1 0 0 1-2.2 0V6.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
const EYE_ICON =
  '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M10 5c-3.5 0-6.2 2.6-7.5 5 1.3 2.4 4 5 7.5 5s6.2-2.6 7.5-5C16.2 7.6 13.5 5 10 5Zm0 8a3 3 0 1 1 0-6 3 3 0 0 1 0 6Z" fill="currentColor"/></svg>';
const DOWNLOAD_ICON =
  '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M10 3v9m0 0-3.5-3.5M10 12l3.5-3.5M4 15.5h12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const TRASH_ICON =
  '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M4.5 6h11M8 6V4.5h4V6m-6 0 .6 9.5h6.8L14 6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

async function uploadFile(file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<{ url: string }>('/uploads/inline', form);
  return data.url;
}

const isImageFile = (file: File) => file.type.startsWith('image/');

/** Sends pasted/dropped/picked images through CKEditor's upload pipeline. */
class PmaUploadAdapter {
  constructor(private loader: { file: Promise<File | null> }) {}
  async upload() {
    const file = await this.loader.file;
    if (!file) throw new Error('No file');
    try {
      return { default: await uploadFile(file) };
    } catch (err) {
      notifyError('Could not upload the image');
      throw err;
    }
  }
  abort() {}
}

/**
 * - Images (paste, drag & drop, or the import button) are uploaded and shown inline.
 * - Other files become a link to the uploaded file (opened by the in-app reader).
 * - Selecting an image shows View / Download / Delete buttons.
 */
class PmaFiles extends Plugin {
  static get requires() {
    return [FileRepository] as const;
  }

  static get pluginName() {
    return 'PmaFiles' as const;
  }

  init() {
    const editor = this.editor;
    editor.plugins.get(FileRepository).createUploadAdapter = (loader) => new PmaUploadAdapter(loader) as never;

    const insertFileLink = (name: string, url: string) => {
      editor.model.change((writer) => {
        editor.model.insertContent(writer.createText(name, { linkHref: url }));
        editor.model.insertContent(writer.createText(' '));
      });
    };

    const handleFiles = async (files: File[]) => {
      for (const file of files) {
        if (isImageFile(file)) {
          editor.execute('uploadImage', { file });
          continue;
        }
        try {
          insertFileLink(file.name, await uploadFile(file));
        } catch {
          notifyError(`Could not upload ${file.name}`);
        }
      }
    };

    // Toolbar: import file
    editor.ui.componentFactory.add('importFile', (locale) => {
      const button = new ButtonView(locale);
      button.set({ label: 'Import file', icon: CLIP_ICON, tooltip: true, withText: false });
      button.on('execute', () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.onchange = () => handleFiles(Array.from(input.files ?? []));
        input.click();
      });
      return button;
    });

    // Paste / drop of non-image files (images are handled by the ImageUpload plugin).
    editor.editing.view.document.on(
      'clipboardInput',
      (evt, data: { dataTransfer?: { files?: FileList } }) => {
        const files = Array.from(data.dataTransfer?.files ?? []);
        const others = files.filter((f) => !isImageFile(f));
        if (others.length === 0) return;
        handleFiles(others);
        if (others.length === files.length) evt.stop();
      },
      { priority: 'high' },
    );

    // Image toolbar buttons
    const selectedSrc = () => (editor.model.document.selection.getSelectedElement()?.getAttribute('src') as string | undefined) ?? '';
    const addImageButton = (name: string, label: string, icon: string, run: () => void) =>
      editor.ui.componentFactory.add(name, (locale) => {
        const button = new ButtonView(locale);
        button.set({ label, icon, tooltip: true, withText: false });
        button.on('execute', run);
        return button;
      });
    const deleteSelected = () => editor.model.change(() => editor.model.deleteContent(editor.model.document.selection));

    addImageButton('pmaImageView', 'View full image', EYE_ICON, () => {
      const url = selectedSrc();
      if (url) openFile({ url, name: url.split('/').pop(), onDelete: deleteSelected });
    });
    addImageButton('pmaImageDownload', 'Download image', DOWNLOAD_ICON, () => {
      const url = selectedSrc();
      if (url) void downloadFile(url, url.split('/').pop());
    });
    addImageButton('pmaImageDelete', 'Delete image', TRASH_ICON, deleteSelected);
  }
}

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  minHeight?: number;
  /** Overrides the people offered for @mentions (defaults to the surrounding MentionProvider). */
  mentionable?: MentionPerson[];
}

export function RichTextEditor({ value, onChange, minHeight = 140, mentionable }: RichTextEditorProps) {
  const contextPeople = useMentionPeople();
  const peopleRef = useRef<MentionPerson[]>(mentionable ?? contextPeople);
  peopleRef.current = mentionable ?? contextPeople;
  const last = useRef(value || '');
  useEffect(() => {
    last.current = value || '';
  }, [value]);

  const config = useMemo(
    () => ({
      licenseKey: process.env.REACT_APP_CKEDITOR_LICENSE_KEY || 'GPL',
      plugins: [Essentials, Paragraph, Bold, Italic, Link, List, Undo, Mention, Image, ImageToolbar, ImageUpload, PasteFromOffice, PmaFiles],
      toolbar: ['undo', 'redo', '|', 'bold', 'italic', 'link', '|', 'bulletedList', 'numberedList', '|', 'importFile'],
      image: { toolbar: ['pmaImageView', 'pmaImageDownload', 'pmaImageDelete'] },
      placeholder: 'Type @ to mention someone — paste or drop images and files here',
      mention: {
        feeds: [
          {
            marker: '@',
            minimumCharacters: 0,
            feed: (query: string) =>
              peopleRef.current
                .filter((p) => p.name.toLowerCase().includes(query.toLowerCase()))
                .slice(0, 8)
                .map((p) => ({ id: `@${p.name}`, userId: p.userId, name: p.name })),
          },
        ],
      },
    }),
    [],
  );

  return (
    <Box
      sx={{
        '& .ck-editor__editable': { minHeight },
        '& .ck.ck-editor__main > .ck-editor__editable': { bgcolor: 'background.paper', color: 'text.primary' },
        '& .ck-content .mention': { fontWeight: 800, color: 'primary.main', bgcolor: 'transparent' },
        '& .ck-content img': { maxWidth: '100%', height: 'auto' },
      }}
    >
      <CKEditor
        editor={ClassicEditor}
        data={value || ''}
        config={config as never}
        onReady={(editor) => {
          // Double-click an uploaded file link or image to read/view it without leaving the page.
          const root = editor.ui.getEditableElement();
          root?.addEventListener('dblclick', (e) => {
            const target = e.target as HTMLElement;
            const link = target.closest('a');
            if (link && isUploadedFileUrl(link.href)) {
              e.preventDefault();
              openFile({ url: link.href, name: link.textContent || undefined });
              return;
            }
            const img = target.closest('img') as HTMLImageElement | null;
            if (img && isImageUrl(img.src)) openFile({ url: img.src, name: img.src.split('/').pop() });
          });
        }}
        onChange={(_, editor) => {
          const html = editor.getData();
          if (html !== last.current) {
            last.current = html;
            onChange(html);
          }
        }}
      />
    </Box>
  );
}
